import { useReducer } from "react";
import { ICartSearchResponseData } from "@/api_framework/api_modals/Cart";

export interface IBarcodeValidationContext {
  item: ICartSearchResponseData;
  swap?: boolean;
  applyMembershipCoupons?: boolean;
  editLotDetails?: boolean;
  isRefreshCart?: boolean;
}

interface BarcodeValidationState {
  isOpen: boolean;
  inputValue: string;
  showMismatchError: boolean;
  pendingValidation: IBarcodeValidationContext | null;
}

type BarcodeValidationAction =
  | { type: "OPEN"; payload: IBarcodeValidationContext }
  | { type: "CLOSE" }
  | { type: "SET_INPUT_VALUE"; payload: string }
  | { type: "SET_MISMATCH_ERROR"; payload: boolean };

const initialBarcodeValidationState: BarcodeValidationState = {
  isOpen: false,
  inputValue: "",
  showMismatchError: false,
  pendingValidation: null,
};

function barcodeValidationReducer(
  state: BarcodeValidationState,
  action: BarcodeValidationAction,
): BarcodeValidationState {
  switch (action.type) {
    case "OPEN":
      return { ...state, pendingValidation: action.payload, inputValue: "", showMismatchError: false, isOpen: true };
    case "CLOSE":
      return { ...state, isOpen: false, inputValue: "", showMismatchError: false, pendingValidation: null };
    case "SET_INPUT_VALUE":
      return { ...state, inputValue: action.payload };
    case "SET_MISMATCH_ERROR":
      return { ...state, showMismatchError: action.payload };
    default:
      return state;
  }
}

// Groups the barcode-mismatch slideover flow (open state, scanned value, mismatch error,
// and the item/context the validation applies to) so opening/closing resets them together.
export const useBarcodeValidationState = () => {
  const [state, dispatch] = useReducer(barcodeValidationReducer, initialBarcodeValidationState);

  return {
    isBarcodeSlideoverOpen: state.isOpen,
    barcodeInputValue: state.inputValue,
    showBarcodeMismatchError: state.showMismatchError,
    pendingBarcodeValidation: state.pendingValidation,
    setBarcodeInputValue: (payload: string) => dispatch({ type: "SET_INPUT_VALUE", payload }),
    setShowBarcodeMismatchError: (payload: boolean) => dispatch({ type: "SET_MISMATCH_ERROR", payload }),
    openBarcodeValidation: (context: IBarcodeValidationContext) => dispatch({ type: "OPEN", payload: context }),
    closeBarcodeValidation: () => dispatch({ type: "CLOSE" }),
  };
};
